import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { type ComponentProps, useState } from 'react';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { DisplayIfVisible } from '@sorare/core/src/atoms/layout/DisplayIfVisible';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
  type ClickMarketHomeSection,
  ClickMarketHomeSection_Section,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { WheelBanner } from '@sorare/marketplace/src/components/Wheel/WheelBanner';
import { BestPerformers } from '@sorare/marketplace/src/components/market-home/BestPerformers';
import {
  BestPredictions,
  PREDICTIONS_SORTS_BY_SPORT,
} from '@sorare/marketplace/src/components/market-home/BestPredictions';
import { Filters } from '@sorare/marketplace/src/components/market-home/Filters';
import { FriendsRecentPurchases } from '@sorare/marketplace/src/components/market-home/FriendsRecentPurchases';
import { MarketRoutesBlock } from '@sorare/marketplace/src/components/market-home/MarketRoutesBlock';
import { PopularWatchlists } from '@sorare/marketplace/src/components/market-home/PopularWatchlists';
import { PromoBanners } from '@sorare/marketplace/src/components/market-home/PromoBanners';
import { TrendingAuctions } from '@sorare/marketplace/src/components/market-home/TrendingAuctions';
import { TrendingPlayers } from '@sorare/marketplace/src/components/market-home/TrendingPlayers';
import { PlayerDrawer } from '@sorare/marketplace/src/components/player/PlayerDrawer';
import type { Props as PlayerDrawerProps } from '@sorare/marketplace/src/components/player/PlayerDrawer';
import {
  PlayerMarketDrawer,
  PlayerMarketDrawerVariant,
} from '@sorare/marketplace/src/components/player/PlayerMarketDrawer';

import { CommonPacksPage } from 'components/commonGame/CommonPacksPage';
import { Collections } from 'components/market-home/Collections';

import type {
  MarketHomePageQuery,
  MarketHomePageQueryVariables,
} from './__generated__/index.graphql';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  container-type: inline-size;
`;

const Layout = styled(Horizontal)`
  gap: 0;
`;
const Main = styled(Vertical)`
  max-width: 100%;
  padding: var(--double-unit) 0 var(--quadruple-unit);
  @media ${laptopAndAbove} {
    padding: var(--double-unit) 0 calc(var(--unit) * 6);
  }

  &.withoutPromoBanners {
    padding-top: calc(8 * var(--unit));
  }
`;

const MARKET_HOME_PAGE_QUERY = gql`
  query MarketHomePageQuery(
    $sport: Sport!
    $so5CompetitionSlug: String!
    $predictionsFilters: String!
    $predictionsSorts: [SearchSortInput!]
    $rarity: Rarity!
    $withTrendingPlayers: Boolean!
  ) {
    market {
      id
      publicWatchlists(
        sport: $sport
        first: 20
        sortBy: MOST_FOLLOWED_WITHIN_SEVEN_DAYS
        so5CompetitionSlug: $so5CompetitionSlug
      ) {
        nodes {
          slug
          ...PopularWatchlists_watchlist
        }
      }
      trendingPlayers(so5CompetitionSlug: $so5CompetitionSlug)
        @include(if: $withTrendingPlayers) {
        nodes {
          id
          tradesCount
          player {
            slug
            ...TrendingPlayers_anyPlayer
          }
        }
      }
    }
    forYou {
      friendsRecentPurchases(
        so5CompetitionSlug: $so5CompetitionSlug
        sport: $sport
        rarity: $rarity
      ) {
        id
        ...FriendsRecentPurchases_tokenOwner
      }
    }
    bestPredictions: searchPlayers(
      query: ""
      page: 1
      pageSize: 30
      advancedFilters: $predictionsFilters
      sorts: $predictionsSorts
    ) {
      ...BestPredictions_searchPlayers
    }
    so5 {
      so5Fixture(type: UPCOMING, sport: $sport) {
        slug
        ...BestPredictions_so5Fixture
      }
    }
    tokens {
      promotionalEvents(sport: $sport) {
        slug
        ...PromoBanners_PromotionalEvent
      }
    }
  }
  ${PopularWatchlists.fragments.watchlist}
  ${FriendsRecentPurchases.fragments.tokenOwner}
  ${BestPredictions.fragments.searchPlayers}
  ${BestPredictions.fragments.so5Fixture}
  ${PromoBanners.fragments.promotionalEvent}
  ${TrendingPlayers.fragments.anyPlayer}
` as TypedDocumentNode<MarketHomePageQuery, MarketHomePageQueryVariables>;

export const MarketHomePage = ({ sport }: { sport: Sport }) => {
  const {
    flags: { useTrendingPlayersOnMarketHome = false },
  } = useFeatureFlags();
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);
  const [marketPlayerSlug, setMarketPlayerSlug] = useState<string | null>(null);
  const [filters, setFilters] =
    useState<ComponentProps<typeof PlayerMarketDrawer>['filters']>(undefined);

  const isSimplifiedNav = useSeriesNav();
  const isLaptopAndAbove = useIsLaptop();
  const { lifecycle } = useLifecycle();

  const rarity = isSimplifiedNav
    ? Rarity.common
    : lifecycle?.marketHomeFilters?.[sport]?.rarity || Rarity.limited;

  const lifecycleSo5CompetitionSlug =
    lifecycle?.marketHomeFilters?.[sport]?.competitionSlug || '';

  const { scoutableSo5Competitions } = useSo5Competitions();
  const { algoliaFilters } = scoutableSo5Competitions.find(
    competition => competition.slug === lifecycleSo5CompetitionSlug
  ) || { algoliaFilters: `sport:${sport.toLowerCase()}` };

  const so5CompetitionSlug =
    lifecycleSo5CompetitionSlug !== 'DEFAULT'
      ? lifecycleSo5CompetitionSlug
      : '';
  const { data, loading } = useQuery(MARKET_HOME_PAGE_QUERY, {
    variables: {
      sport,
      so5CompetitionSlug,
      predictionsFilters: joinFiltersWithAnd([algoliaFilters]),
      predictionsSorts: PREDICTIONS_SORTS_BY_SPORT[sport],
      rarity,
      withTrendingPlayers: useTrendingPlayersOnMarketHome,
    },
    skip: rarity === Rarity.common,
  });

  const { publicWatchlists } = data?.market || {};
  const { so5Fixture } = data?.so5 || {};

  const track = useEvents();

  const createTrackMarketHomeSection = (
    origin: ClickMarketHomeSection_Section
  ) => {
    return (
      props: Omit<
        ClickMarketHomeSection,
        'origin' | 'sport' | 'scarcity' | 'so5CompetitionSlug'
      >
    ) => {
      track('Click Market Home Section', {
        so5CompetitionSlug,
        scarcity: rarity,
        sport,
        origin,
        ...props,
      });
    };
  };
  const withoutPromoBanners =
    (data?.tokens.promotionalEvents.filter(p => p.pictureUrl)?.length ?? 0) ===
    0;
  if (rarity === Rarity.custom_series) return null;

  const onMarketClick = (slug: string, seasonality: SeasonEligibility) => {
    setMarketPlayerSlug(slug);
    if (rarity !== Rarity.common) {
      setFilters({
        seasonality,
        rarity,
      });
    }
  };

  const onStatsClick = (slug: string) => {
    setCardOpened({ cardSlug: '', playerSlug: slug });
  };

  return (
    <Root>
      <WheelBanner rarity={rarity} />
      <Filters sport={sport} />
      {rarity === Rarity.common ? (
        <CommonPacksPage />
      ) : (
        <>
          <PromoBanners
            loading={loading}
            promotionalEvents={data?.tokens.promotionalEvents || []}
            sport={sport}
          />
          <Layout>
            <Main
              gap={isLaptopAndAbove ? 6 : 5}
              className={classNames({ withoutPromoBanners })}
            >
              <MarketRoutesBlock sport={sport} rarity={rarity} />
              <FriendsRecentPurchases
                onMarketClick={onMarketClick}
                tokenOwners={data?.forYou?.friendsRecentPurchases || []}
                track={createTrackMarketHomeSection(
                  ClickMarketHomeSection_Section.FRIENDS_RECENT_PURCHASES
                )}
              />
              <BestPredictions
                predictions={data?.bestPredictions}
                loading={loading}
                sport={sport}
                so5Fixture={so5Fixture}
                onMarketClick={slug =>
                  onMarketClick(slug, SeasonEligibility.IN_SEASON)
                }
                onStatsClick={onStatsClick}
                track={createTrackMarketHomeSection(
                  ClickMarketHomeSection_Section.BEST_PREDICTIONS
                )}
              />
              {!!useTrendingPlayersOnMarketHome && (
                <TrendingPlayers
                  loading={loading}
                  players={data?.market?.trendingPlayers?.nodes}
                  onMarketClick={slug =>
                    onMarketClick(slug, SeasonEligibility.IN_SEASON)
                  }
                  onStatsClick={onStatsClick}
                  track={createTrackMarketHomeSection(
                    ClickMarketHomeSection_Section.TRENDING_PLAYERS
                  )}
                />
              )}
              <TrendingAuctions
                sport={sport}
                track={createTrackMarketHomeSection(
                  ClickMarketHomeSection_Section.TRENDING_AUCTIONS
                )}
              />
              <DisplayIfVisible>
                <BestPerformers
                  onMarketClick={slug =>
                    onMarketClick(slug, SeasonEligibility.IN_SEASON)
                  }
                  onStatsClick={onStatsClick}
                  track={createTrackMarketHomeSection(
                    ClickMarketHomeSection_Section.TOP_PERFORMERS
                  )}
                />
              </DisplayIfVisible>
              <DisplayIfVisible>
                <Collections
                  so5CompetitionSlug={so5CompetitionSlug}
                  sport={sport}
                  rarity={rarity}
                  track={createTrackMarketHomeSection(
                    ClickMarketHomeSection_Section.COLLECTION
                  )}
                />
              </DisplayIfVisible>
              <PopularWatchlists
                watchlists={publicWatchlists?.nodes || []}
                track={createTrackMarketHomeSection(
                  ClickMarketHomeSection_Section.WATCHLISTS
                )}
              />
            </Main>
          </Layout>
        </>
      )}
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
      />
      <PlayerMarketDrawer
        onClose={() => setMarketPlayerSlug(null)}
        playerSlug={marketPlayerSlug}
        filters={filters}
        sport={sport}
        canUseConversionCredit
        origin={{
          instantBuy: ClickInstantBuy_Flow.MARKETPLACE_HOME,
          addToCart: ClickAddToCart_Origin.MARKETPLACE_HOME,
        }}
        variant={PlayerMarketDrawerVariant.MARKET_HOME}
      />
    </Root>
  );
};
